import { useState, useEffect, useContext } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, ArrowLeft, CreditCard, CheckCircle, 
  AlertCircle, ExternalLink, Loader2, DollarSign
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { API, AuthContext } from "../../App";

const OnboardingPayments = () => {
  const navigate = useNavigate();
  const { onboardingData, refreshData } = useOutletContext();
  const { refreshUser } = useContext(AuthContext);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const stripeStatus = onboardingData?.photographer?.stripe_connect_status || "not_connected";
  const stripeAccountId = onboardingData?.photographer?.stripe_connect_account_id;

  const handleConnectStripe = async () => {
    setConnecting(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const currentUrl = window.location.origin;
      const response = await axios.post(`${API}/stripe/connect/onboard`, {
        return_url: `${currentUrl}/dashboard/onboarding/payments?stripe=complete`,
        refresh_url: `${currentUrl}/dashboard/onboarding/payments?stripe=refresh`
      }, { headers });
      
      // If there's an onboarding URL, redirect to it
      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
        return; // Don't setConnecting(false) since we're navigating away
      }
      
      // If account is already complete, refresh data
      if (response.data.status === "complete") {
        toast.success("Stripe account already connected!");
        await refreshData();
        setConnecting(false);
        return;
      }
      
      // Unexpected response
      toast.error("Unexpected response from Stripe setup");
      setConnecting(false);
    } catch (error) {
      console.error("Stripe connect error:", error);
      toast.error(error.response?.data?.detail || "Failed to start Stripe setup");
      setConnecting(false);
    }
  };

  const handleContinue = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(`${API}/onboarding/step/payments`, {}, { headers });
      toast.success("Payments step completed!");
      await refreshData();
      if (refreshUser) await refreshUser();
      navigate("/dashboard/onboarding/publish");
    } catch (error) {
      toast.error("Failed to continue");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate("/dashboard/onboarding/portfolio");
  };

  const getStatusBadge = () => {
    switch (stripeStatus) {
      case "active":
        return (
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
            <CheckCircle className="w-3 h-3 mr-1" />
            Connected
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
            <AlertCircle className="w-3 h-3 mr-1" />
            Pending Setup
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-muted-foreground">
            Not Connected
          </Badge>
        );
    }
  };

  return (
    <div data-testid="onboarding-step-4">
      <div className="mb-8">
        <h1 className="text-3xl font-serif mb-2">Payment Setup</h1>
        <p className="text-muted-foreground">
          Connect Stripe to accept payments and sell photos. You can skip this and set it up later.
        </p>
      </div>

      <div className="space-y-6">
        {/* Stripe Connect Card */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Stripe Connect
              </CardTitle>
              {getStatusBadge()}
            </div>
            <CardDescription>
              Stripe handles all payments securely. You&apos;ll receive payouts directly to your bank account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {stripeStatus === "active" ? (
              <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-400">You&apos;re all set!</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Your Stripe account is connected and ready to accept payments.
                    </p>
                  </div>
                </div>
              </div>
            ) : stripeStatus === "pending" ? (
              <div className="space-y-4">
                <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-400">Setup Incomplete</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Please complete your Stripe account setup to start accepting payments.
                      </p>
                    </div>
                  </div>
                </div>
                <Button onClick={handleConnectStripe} disabled={connecting}>
                  {connecting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      Continue Setup
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <Button onClick={handleConnectStripe} disabled={connecting} size="lg">
                  {connecting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Connect with Stripe
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground">
                  You&apos;ll be redirected to Stripe to securely set up your account
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Benefits Card */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              What You Can Do with Stripe
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {[
                "Accept credit/debit card payments from clients",
                "Sell individual photos and photo packages",
                "Send invoices with online payment links",
                "Receive payouts directly to your bank account",
                "Track all transactions in one place"
              ].map((benefit, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{benefit}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-muted/30 border-border">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> You can publish your profile without connecting Stripe. 
              Payment features will be enabled once you complete the Stripe setup from your dashboard.
            </p>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleContinue} disabled={saving} data-testid="continue-btn">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                {stripeStatus === "active" ? "Continue" : "Skip for Now"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPayments;
